// The redesigned service-user HFP report (Figma: RMS · Holistic Functional
// Profile, READY FOR DEV 8 Sep 2026). One app serves both layouts: mobile 440
// (bottom navigation, sheets) and desktop 1720 / Air 1280 (plum top bar,
// 1080px content column, flyout and meaning modals).
//
// Entry points into the report do not change: this renders inside
// /rms/patient/:patientRecordId/hfp and Back returns to the child's record.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './hfpReport.css';
import buildHfpDisplayModel from './buildHfpDisplayModel';
import { SECTIONS, SECTION_CHIP_LABELS } from './hfpDisplay';
import {
  MobileAppBar,
  MobileTopTabs,
  MobileNextBar,
  MobileBottomNav,
  DesktopTopBar,
  DesktopSectionRow,
  DesktopPagerBar,
  LegalNotice,
  PageHead,
} from './components/chrome';
import {
  AreaCard,
  MobileGuideCard,
  DesktopGuideCard,
  guideCards,
  BulletCard,
  TextCard,
  SkeletonCard,
  ErrorCard,
  EmptyState,
  LinkRow,
  SafetyNoteCard,
} from './components/cards';
import HelpHubList from './components/helpHub';
import {
  Sheet,
  AreaHelpContent,
  LevelsSheetContent,
  ReadingOptionsContent,
  MeaningModal,
  DesktopFlyout,
} from './components/overlays';

// The PDF report is on hold as a piece of work (Figma build notes, 8 Sep):
// no download pipeline exists yet, so the Download controls stay off until
// version one of the PDF ships. The toast components are built and tested
// ready for it (components/overlays.js Snackbar).
export const HFP_PDF_DOWNLOAD_ENABLED = false;

const READING_PREFS_KEY = 'td.hfp.reading.v1';

function loadReadingPrefs() {
  try {
    const raw = window.localStorage.getItem(READING_PREFS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return {
      textScale: [1, 1.25, 1.5].includes(parsed?.textScale) ? parsed.textScale : 1,
      lineSpacing: [1, 1.35].includes(parsed?.lineSpacing) ? parsed.lineSpacing : 1,
    };
  } catch (err) {
    return { textScale: 1, lineSpacing: 1 };
  }
}

function useIsDesktop() {
  const query = '(min-width: 1024px)';
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsDesktop(e.matches);
    // Older WebKit (iOS <= 13) only implements the addListener API.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);
  return isDesktop;
}

const SECTION_KEYS = SECTIONS.map((s) => s.key);

function partForPage(page) {
  if (page === 'guide') return 'guide';
  if (page === 'help-home' || page === 'help-school' || page === 'now') return 'help';
  return 'profile';
}

/**
 * @param {object} props
 * @param {'loading'|'ready'|'not_ready'|'error'} props.narrativesState  outcome
 *   of the rmsGenerateHfpV3Report callable, mapped by the page from the
 *   backend's typed statuses.
 */
export default function HfpReportApp({
  model,
  childName,
  pronounSet,
  narrativesState = 'loading',
  onRetryNarratives,
  onBack,
}) {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [searchParams, setSearchParams] = useSearchParams();

  const narrativesArrived = narrativesState === 'ready';
  const narrativesLoading = narrativesState === 'loading';

  const display = useMemo(
    () => buildHfpDisplayModel(model, childName, pronounSet, { narrativesArrived }),
    [model, childName, pronounSet, narrativesArrived]
  );

  // ---- page state (in the URL so browser back works) ----------------------
  const pageOrder = useMemo(() => {
    const pages = ['guide', ...SECTION_KEYS, 'help-home', 'help-school'];
    if (!isDesktop) pages.push('now');
    return pages;
  }, [isDesktop]);

  const rawPage = searchParams.get('hfpPage') || 'guide';
  // Desktop has no "What to do now" page; a shared mobile link lands on the
  // nearest content (the school help hub) rather than silently resetting.
  const page = pageOrder.includes(rawPage)
    ? rawPage
    : rawPage === 'now' && isDesktop
      ? 'help-school'
      : 'guide';
  const part = partForPage(page);

  const goTo = useCallback(
    (nextPage) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (nextPage === 'guide') params.delete('hfpPage');
          else params.set('hfpPage', nextPage);
          return params;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page]);

  // ---- reading options (persist on this device only) -----------------------
  const [reading, setReading] = useState(loadReadingPrefs);
  const updateReading = useCallback((patch) => {
    setReading((prev) => {
      const next = { ...prev, ...patch };
      try {
        window.localStorage.setItem(READING_PREFS_KEY, JSON.stringify(next));
      } catch (err) {
        /* per-viewer convenience only */
      }
      return next;
    });
  }, []);

  // ---- overlays -------------------------------------------------------------
  const [helpSheet, setHelpSheet] = useState(null); // { group, setting }
  const [levelsOpen, setLevelsOpen] = useState(false);
  const [readingOpen, setReadingOpen] = useState(false);
  const [meaning, setMeaning] = useState(null); // { area, setting, level }
  const [flyout, setFlyout] = useState(null); // { setting, index }

  const orderedGroups = useCallback(
    (setting) => [...display.help[setting].priority, ...display.help[setting].support],
    [display]
  );

  const handleBack = useCallback(() => {
    if (onBack) onBack();
    else navigate(-1);
  }, [onBack, navigate]);

  // ---- navigation labels ---------------------------------------------------
  const pageIndex = pageOrder.indexOf(page);
  const sectionIndex = SECTION_KEYS.indexOf(page);
  const section = sectionIndex >= 0 ? display.sections[sectionIndex] : null;

  const mobileNextLabel = (() => {
    if (page === 'now') return HFP_PDF_DOWNLOAD_ENABLED ? 'Download this report as a PDF' : `${display.childName}’s record`;
    const next = pageOrder[pageIndex + 1];
    if (!next) return null;
    if (next === 'help-home') return 'What could help';
    if (next === 'help-school') return 'What could help at school';
    if (next === 'now') return 'What to do now';
    return display.sections[SECTION_KEYS.indexOf(next)].title;
  })();

  const handleMobileNext = () => {
    if (page === 'now') {
      if (HFP_PDF_DOWNLOAD_ENABLED) return; // PDF flow (behind the flag)
      handleBack();
      return;
    }
    const next = pageOrder[pageIndex + 1];
    if (next) goTo(next);
  };

  const desktopPager = (() => {
    const labelFor = (key) => {
      if (key === 'guide') return 'How to use this report';
      if (key === 'help-home') return 'Adjustments · Home';
      if (key === 'help-school') return 'Adjustments · School';
      if (key === 'now') return 'What to do now';
      return display.sections[SECTION_KEYS.indexOf(key)].title;
    };
    const prevKey = pageOrder[pageIndex - 1];
    const nextKey = pageOrder[pageIndex + 1];
    return {
      previous: prevKey
        ? { key: prevKey, label: labelFor(prevKey) }
        // TDI-501: pathway-neutral. This report is reached by SEND-only
        // families too, and calling their child's page "their referral" told
        // them of a referral that does not exist.
        : { key: 'record', label: `Back to ${display.childName}’s page` },
      next: nextKey ? { key: nextKey, label: labelFor(nextKey) } : null,
    };
  })();

  // ---- shared help-list loading / error / empty ----------------------------
  const renderHelpListBody = (setting, renderList) => {
    if (narrativesState === 'error' || (narrativesArrived && !display.adjustmentsReady)) {
      // A failed call, or a payload that arrived without its adjustments
      // block (the backend tolerates a matcher failure): honest error + retry.
      return (
        <ErrorCard
          title="We could not load what could help"
          body="Check your connection and try again. Nothing you have read is lost."
          onRetry={onRetryNarratives}
        />
      );
    }
    if (narrativesState === 'not_ready') {
      return (
        <EmptyState
          title="The ideas are not ready yet"
          body="The profile is complete and readable now. The ideas appear here once the report has finished preparing."
        />
      );
    }
    if (!narrativesArrived) {
      return (
        <div className="flex w-full flex-col gap-[16px]">
          <p className="hfp-meta-14 text-[color:var(--hfp-ink-mid)]" aria-live="polite">
            Preparing the ideas — the profile is complete and readable now.
          </p>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      );
    }
    if (orderedGroups(setting).length === 0) {
      const other = setting === 'home' ? 'school' : 'home';
      const otherHasIdeas = orderedGroups(other).length > 0;
      const settingLabel = setting === 'home' ? 'Home' : 'School';
      return (
        <EmptyState
          title={`Nothing to show at ${setting} yet`}
          body={
            `${settingLabel} did not mark any area as a priority or a support need, so there are no ideas for ${setting}.` +
            (otherHasIdeas
              ? ` The ${other} ideas are on the ${setting === 'home' ? 'next' : 'previous'} page, and the profile shows what ${setting} did record.`
              : ` The profile shows what ${setting} did record.`)
          }
        />
      );
    }
    return renderList();
  };

  // ---- screens --------------------------------------------------------------
  const guideCardsList = useMemo(
    () => guideCards({ childName: display.childName, showPdfLink: HFP_PDF_DOWNLOAD_ENABLED }),
    [display.childName]
  );
  const [openGuideCard, setOpenGuideCard] = useState(3);

  const renderGuide = () =>
    isDesktop ? (
      <div className="flex flex-col gap-[12px] w-full">
        <PageHead
          desktop
          title="How to use this report"
          lede="A five-minute guide for parents, carers and school staff. You only need to read it once."
        />
        <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">
          Here are five things to know before you read {display.childName}’s report.
        </p>
        {guideCardsList.map((card) => (
          <DesktopGuideCard
            key={card.number}
            card={card}
            open={openGuideCard === card.number}
            onToggle={() => setOpenGuideCard((prev) => (prev === card.number ? null : card.number))}
          />
        ))}
        <LegalNotice calibrationNote={display.calibrationNote} />
      </div>
    ) : (
      <div className="flex flex-col gap-[16px] w-full">
        <PageHead title="How to use this report" lede="Five things to know, about two minutes" />
        <p className="hfp-body-16 w-full text-[color:var(--hfp-ink-mid)]">
          For parents, carers and school staff. Read it once, then go to the profile.
        </p>
        {guideCardsList.map((card) => (
          <MobileGuideCard key={card.number} card={card} onLevelsLink={() => setLevelsOpen(true)} />
        ))}
      </div>
    );

  const renderSection = () => (
    <div className="flex flex-col w-full gap-[16px]">
      <PageHead
        desktop={isDesktop}
        eyebrow={section.eyebrow}
        title={section.title}
        lede={section.lede}
      />
      {!isDesktop && <LinkRow label="What the levels mean" onClick={() => setLevelsOpen(true)} />}
      {section.areas.map((area) => (
        <AreaCard
          key={area.id}
          area={area}
          desktop={isDesktop}
          storyLoading={narrativesLoading && !area.story}
          storyUnavailable={!narrativesLoading && !area.story}
          onHelp={(a, setting) => setHelpSheet({ group: a.help[setting], setting })}
          onOpenMeaning={isDesktop ? (a, setting) => setMeaning({ area: a, setting, level: a[setting] }) : undefined}
        />
      ))}
      {section.internal === 'Sensory' && display.painSafety && (
        <SafetyNoteCard childName={display.childName} />
      )}
      {isDesktop && <LegalNotice calibrationNote={display.calibrationNote} />}
    </div>
  );

  const renderHelpList = (setting) => (
    <div className={`flex flex-col w-full ${isDesktop ? 'gap-[16px]' : 'gap-[12px]'}`}>
      {isDesktop ? (
        <PageHead
          desktop
          title={`Suggested Adjustments at ${setting === 'home' ? 'Home' : 'School'}`}
          lede={`What could be done at ${setting} to set ${display.childName} up for success. In priority order from ${display.pronouns.poss} profile; not every area gets an adjustment.`}
        />
      ) : (
        <PageHead title={`What could help at ${setting}`} lede={display.helpLedes[setting]} />
      )}
      {renderHelpListBody(setting, () => (
        <HelpHubList
          help={display.help[setting]}
          setting={setting}
          desktop={isDesktop}
          onOpen={(group, rank) =>
            isDesktop ? setFlyout({ setting, index: rank }) : setHelpSheet({ group, setting })
          }
        />
      ))}
      {isDesktop && <LegalNotice calibrationNote={display.calibrationNote} />}
    </div>
  );

  const renderNow = () => (
    <div className="flex flex-col gap-[16px] w-full">
      <PageHead eyebrow="Home and school together" title="What to do now" />
      <BulletCard
        title="Before you talk together"
        bullets={[
          'Note anything that surprised you.',
          'Where home and school differ, ask why. Neither is wrong.',
          'Agree two or three things to try, who will do them, and when you will check in.',
        ]}
      />
      <TextCard
        title="The ideas to pick from"
        body="Home ideas come from the home answers and school ideas from the school answers. They are kept separate on purpose."
      />
      <TextCard
        title="Take it with you"
        body="Take the whole report to the meeting, on paper or on your phone. It includes what could help."
      />
    </div>
  );

  const renderPage = () => {
    if (page === 'guide') return renderGuide();
    if (page === 'help-home') return renderHelpList('home');
    if (page === 'help-school') return renderHelpList('school');
    if (page === 'now') return renderNow();
    return renderSection();
  };

  // ---- chrome ---------------------------------------------------------------
  const mobileTopTabs = (() => {
    if (part === 'profile') {
      return display.sections.map((s) => ({ key: s.key, label: s.title }));
    }
    if (part === 'help') {
      return [
        { key: 'help-home', label: 'At home' },
        { key: 'help-school', label: 'At school' },
        { key: 'now', label: 'What to do now' },
      ];
    }
    return null; // the guide has no top tabs
  })();

  const desktopChips = (() => {
    if (part === 'profile') {
      return {
        chips: display.sections.map((s) => ({ key: s.key, label: SECTION_CHIP_LABELS[s.key] || s.title })),
        positionLabel: `${sectionIndex + 1} of ${display.sections.length}`,
      };
    }
    if (part === 'help') {
      return {
        chips: [
          { key: 'help-home', label: 'At home' },
          { key: 'help-school', label: 'At school' },
        ],
        positionLabel: null,
      };
    }
    return null;
  })();

  const selectPart = (nextPart) => {
    if (nextPart === 'guide') goTo('guide');
    else if (nextPart === 'profile') goTo(SECTION_KEYS[0]); // The profile lands on Executive Functions
    else goTo('help-home');
  };

  const flyoutGroups = flyout ? orderedGroups(flyout.setting) : [];
  const flyoutGroup = flyout ? flyoutGroups[flyout.index] : null;

  const readingSpecimen = useMemo(() => {
    const story = display.sections.flatMap((s) => s.areas).find((a) => a.story)?.story;
    if (!story) return `This is how the report text will look for ${display.childName}.`;
    const firstSentence = story.match(/^[^.!?]*[.!?]/);
    return firstSentence ? firstSentence[0] : story;
  }, [display]);

  const rootStyle = { '--hfp-rs': reading.textScale, '--hfp-ls': reading.lineSpacing };

  return (
    <div className="hfp-report flex h-full min-h-0 flex-col" style={rootStyle}>
      {isDesktop ? (
        <>
          <DesktopTopBar
            childName={display.childName}
            currentPart={part}
            onSelectPart={selectPart}
            onBack={handleBack}
            showDownload={HFP_PDF_DOWNLOAD_ENABLED}
          />
          {desktopChips && (
            <DesktopSectionRow
              chips={desktopChips.chips}
              currentKey={page}
              onSelect={goTo}
              positionLabel={desktopChips.positionLabel}
            />
          )}
          <div ref={scrollRef} className="flex flex-1 min-h-0 flex-col items-center overflow-y-auto px-[24px] pt-[28px] pb-[32px]">
            <main key={page} className="hfp-tab-anim w-full max-w-[1080px]">
              {renderPage()}
              <div aria-hidden="true" className="h-[40px]" />
            </main>
          </div>
          <DesktopPagerBar
            previous={desktopPager.previous}
            next={desktopPager.next}
            onPrevious={() => (desktopPager.previous.key === 'record' ? handleBack() : goTo(desktopPager.previous.key))}
            onNext={() => desktopPager.next && goTo(desktopPager.next.key)}
          />
        </>
      ) : (
        <>
          <MobileAppBar
            onBack={handleBack}
            onReadingOptions={() => setReadingOpen(true)}
            showDownload={HFP_PDF_DOWNLOAD_ENABLED}
          />
          {mobileTopTabs && <MobileTopTabs tabs={mobileTopTabs} currentKey={page} onSelect={goTo} />}
          <main ref={scrollRef} key={page} className="hfp-tab-anim flex-1 min-h-0 overflow-y-auto px-[16px] pt-[28px] pb-[24px]">
            {renderPage()}
            <div aria-hidden="true" className="h-[24px]" />
            {/* Frame-faithful placement: the notice rides at the foot of the
                scrolling content, per the READY FOR DEV frames (flagged in the
                PR — the previous page pinned it in an always-visible footer). */}
            <LegalNotice calibrationNote={display.calibrationNote} />
          </main>
          <MobileNextBar label={mobileNextLabel} onNext={handleMobileNext} download={page === 'now' && HFP_PDF_DOWNLOAD_ENABLED} />
          <MobileBottomNav currentPart={part} onSelect={selectPart} />
        </>
      )}

      {/* ---- overlays ---- */}
      {!isDesktop && helpSheet?.group && (
        <Sheet
          open
          onOpenChange={(open) => !open && setHelpSheet(null)}
          title={`${helpSheet.group.areaName} at ${helpSheet.setting}`}
          tall
          readingStyle={rootStyle}
        >
          <AreaHelpContent group={helpSheet.group} setting={helpSheet.setting} childName={display.childName} />
        </Sheet>
      )}
      {isDesktop && helpSheet?.group && (
        <DesktopFlyout
          open
          onOpenChange={(open) => !open && setHelpSheet(null)}
          group={helpSheet.group}
          setting={helpSheet.setting}
          childName={display.childName}
          readingStyle={rootStyle}
          calibrationNote={display.calibrationNote}
        />
      )}
      <Sheet open={levelsOpen} onOpenChange={setLevelsOpen} title="What the levels mean" readingStyle={rootStyle}>
        <LevelsSheetContent />
      </Sheet>
      <Sheet open={readingOpen} onOpenChange={setReadingOpen} title="Reading options" readingStyle={rootStyle}>
        <ReadingOptionsContent
          specimen={readingSpecimen}
          textScale={reading.textScale}
          lineSpacing={reading.lineSpacing}
          onChange={updateReading}
        />
      </Sheet>
      <MeaningModal
        open={Boolean(meaning)}
        onOpenChange={(open) => !open && setMeaning(null)}
        area={meaning?.area}
        setting={meaning?.setting}
        level={meaning?.level}
        group={meaning ? meaning.area.help[meaning.setting] : null}
        readingStyle={rootStyle}
      />
      {flyoutGroup && (
        <DesktopFlyout
          open
          onOpenChange={(open) => !open && setFlyout(null)}
          group={flyoutGroup}
          setting={flyout.setting}
          childName={display.childName}
          previousLabel={flyout.index > 0 ? flyoutGroups[flyout.index - 1].areaName : null}
          nextLabel={flyout.index < flyoutGroups.length - 1 ? flyoutGroups[flyout.index + 1].areaName : null}
          onPrevious={() => flyout.index > 0 && setFlyout({ ...flyout, index: flyout.index - 1 })}
          onNext={() => flyout.index < flyoutGroups.length - 1 && setFlyout({ ...flyout, index: flyout.index + 1 })}
          readingStyle={rootStyle}
          calibrationNote={display.calibrationNote}
        />
      )}
    </div>
  );
}
